import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Logo } from "@/components/Logo";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { Sparkles, Target, TrendingUp, ArrowRight, Loader2, Frown, MessageCircle } from "lucide-react";

export default function PublicReportPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/public/report/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "This report is unavailable."));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-[#0B0F17] text-white flex flex-col items-center justify-center px-6 text-center" data-testid="public-report-error">
      <Seo title="Report unavailable" noindex />
      <Frown className="h-10 w-10 text-slate-500" />
      <h1 className="mt-4 font-display text-2xl font-bold">Report unavailable</h1>
      <p className="mt-2 max-w-sm text-slate-400">{err}</p>
      <button data-testid="public-report-create-cta" onClick={() => navigate("/auth")}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016]">
        Create your own — free <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center text-slate-400">
      <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
    </div>
  );

  const radar = (data.dimensions || []).map((d) => ({ dim: d.name, value: d.score }));

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white" data-testid="public-report-page">
      <Seo title={`${data.first_name}'s Career Intelligence Profile`} path={`/r/${token}`}
        image={`${window.location.origin}/api/public/report/${token}/card.png`}
        description={`${data.first_name}'s AI Career Intelligence Profile on EntranceZone AI — top strengths, career clusters and best-fit careers. Discover your own for free.`} />
      <header className="sticky top-0 z-40 glass">
        <div className="mx-auto max-w-4xl px-4 sm:px-8 flex items-center justify-between py-4">
          <Logo />
          <button data-testid="public-report-header-cta" onClick={() => navigate("/auth")}
            className="rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-bold text-[#041016] transition-transform hover:scale-105">
            Create your own
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 sm:px-8 py-12">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-500 glow-cyan">
            <Sparkles className="h-8 w-8 text-[#041016]" />
          </div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Career Intelligence Profile</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{data.name}</h1>
          {data.summary && <p className="mt-3 text-slate-400 max-w-xl mx-auto">{data.summary}</p>}
          {data.clarity_score != null && (
            <span className="mt-3 inline-block rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-300">{data.clarity_score}% AI confidence</span>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
            <h3 className="font-display text-lg font-semibold mb-4">Strengths</h3>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <RadarChart data={radar} outerRadius="72%">
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} stroke="rgba(255,255,255,0.08)" />
                  <Radar dataKey="value" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
            <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Target className="h-5 w-5 text-cyan-400" /> Top career clusters</h3>
            <div className="space-y-4">
              {(data.clusters || []).map((c, i) => (
                <div key={c.name}>
                  <div className="flex justify-between text-sm mb-1"><span className="font-medium text-white">{i + 1}. {c.name}</span><span className="font-mono-data text-emerald-300">{c.score}%</span></div>
                  <div className="h-2 rounded-full bg-white/8 overflow-hidden"><motion.div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400" initial={{ width: 0 }} animate={{ width: `${c.score}%` }} transition={{ duration: 0.8 }} /></div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <h4 className="font-display text-sm font-semibold mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-400" /> Best-fit careers</h4>
              <div className="flex flex-wrap gap-2">
                {(data.careers || []).map((c) => (
                  <span key={c.title} className="rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] px-3 py-1 text-xs text-cyan-200">{c.title} · {c.match}%</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 relative overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-br from-[#0F2A33] to-[#131C2E] p-8 sm:p-12 text-center">
          <div className="absolute inset-0 grid-texture opacity-30" />
          <div className="relative">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Discover your own career strengths</h2>
            <p className="mt-3 text-slate-300 max-w-lg mx-auto">Take the free adaptive AI Career Assessment and get a profile like this in minutes.</p>
            <button data-testid="public-report-footer-cta" onClick={() => navigate("/auth")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-3.5 text-base font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
              <Sparkles className="h-5 w-5" /> Start Free Assessment
            </button>
          </div>
        </div>
        <p className="mt-8 text-center text-[11px] text-slate-500">AI-generated guidance — not a guaranteed career outcome. Shared by the profile owner via EntranceZone AI.</p>
      </div>
    </div>
  );
}
